import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Empty, Input, Layout, Row, Select, Spin, Table, Tag, Timeline, Tooltip, Typography } from 'antd'
import { ApiOutlined, ArrowLeftOutlined, QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { getAicDashboardSummary, getAicDashboardDatasets, getAicDashboardDatasetDetail, getAicDashboardExceptions, getRuntimeAssetExceptions } from '../../request'
import VibeCodexGuideDialog from '../vibe/VibeCodexGuideDialog'
import { VIBE_CODEX_GUIDE_SCENE } from '../vibe/vibeCodexGuide'
import AicReliabilitySettings from './AicReliabilitySettings'
import RuntimeAssets from './RuntimeAssets'
import ProjectData from './ProjectData'
import ProjectDictionary from './ProjectDictionary'
import {
  display,
  formatDate,
  formatDateTime,
  formatInteger,
  formatUpdateFrequency,
  getHealthStatus,
  healthMeta,
  healthyRateSummary,
  normalizeOverviewSummary,
  settleRequest,
  unwrapList
} from './aicDashboardUtils'
import './index.less'

const pendingText = <span className="aic-pending-result">未配置</span>
const waitingText = <span className="aic-pending-result">待检测</span>
// 「健康状态」只看上传及时性：有导入计划按计划截止时间判，没有则按"上次上传 + 一个更新周期"判。
const HEALTH_TOOLTIP = '是否按约定时间上传：有导入计划按计划截止时间判断，没有计划则按"上次成功上传 + 一个更新周期"判断。与数据质量、数据日期无关。'
const healthTitle = text => <span>{text} <Tooltip title={HEALTH_TOOLTIP}><QuestionCircleOutlined className="aic-muted" /></Tooltip></span>
const AIC_SKILL_ENTRY_PATH = '/portal/profile?tab=aicassets'

const importPeriodLabels = {
  daily: '日',
  weekly: '周',
  monthly: '月',
  irregular: '不定期'
}

const scheduleStatusLabels = {
  pending: '待上传',
  satisfied: '已按期上传',
  overdue: '已超期',
  cancelled: '已取消'
}

const validationStatusLabels = {
  passed: '校验通过',
  valid: '校验通过',
  failed: '校验失败',
  pending: '待校验'
}

const reliabilityStatusMeta = status => {
  if (status === 'healthy') return { status: 'success', label: '未发现异常' }
  if (status === 'warning') return { status: 'warning', label: '发现异常' }
  if (status === 'check_failed') return { status: 'error', label: '执行失败' }
  if (status === 'unknown') return { status: 'default', label: '暂无结果' }
  return { status: 'default', label: '待检测' }
}

const qualityResultText = item => {
  if (item.qualityStatus === 'healthy') return '未发现异常数据'
  if (item.qualityStatus === 'warning') return `发现 ${item.qualityErrorCount ?? 0} 条异常数据`
  if (item.qualityStatus === 'check_failed') return display(item.qualityErrorSummary || item.qualityErrorCode || '规则执行失败')
  if (item.qualityStatus === 'unknown') return '本次检测未取得有效结果'
  return '规则配置并执行后展示真实结果'
}

/** 导航栏与详情页共用的「获取 AIC Skill」入口，保证两处文案、样式和跳转一致。 */
function AicSkillEntryButton () {
  const history = useHistory()
  return <Button
    className="aic-nav-skill-entry"
    type="primary"
    icon={<ApiOutlined />}
    aria-label="前往 AIC 数据资产获取 AIC Skill"
    onClick={() => history.push(AIC_SKILL_ENTRY_PATH)}
  >
    <span className="aic-nav-skill-label"><span className="aic-nav-skill-prefix">获取 </span>AIC Skill</span>
  </Button>
}

function Nav ({ active }) {
  const history = useHistory()
  const links = [
    ['overview', '总览', '/portal/aic-dashboard/overview', false],
    ['datasets', '数据源', '/portal/aic-dashboard/datasets', false],
    ['assets', '运行资产', '/portal/aic-dashboard/assets', false],
    ['exceptions', '异常中心', '/portal/aic-dashboard/exceptions', false],
    ['settings', '设置', '/portal/aic-dashboard/settings', true]
  ]
  return <div className="aic-nav">
    <div className="aic-logo">AIC</div>
    <div className="aic-nav-links">
      {links.map(([key, label, path, disabled]) => <button
        key={key}
        className={!disabled && active === key ? 'active' : ''}
        disabled={disabled}
        title={disabled ? '暂未开放' : undefined}
        onClick={() => history.push(path)}
      >
        {label}
        {disabled && <span className="aic-nav-disabled-note">暂未开放</span>}
      </button>)}
    </div>
    <div className="aic-nav-skill-group">
      <Button
        className="aic-nav-terminal-entry"
        aria-label="切换到新版 AIC 看板"
        onClick={() => history.push('/portal/aic-dashboard/v2/overview')}
      >
        新版看板
      </Button>
      <span className="aic-nav-skill-hint">AIC数据登记、数据上传、查询、配置检查规则，都使用右侧的Skill</span>
      <AicSkillEntryButton />
    </div>
  </div>
}

function Header ({ title, subtitle, active }) {
  return <>
    <Nav active={active} />
    <div className="aic-page-header">
      <Typography.Title level={1}>{title}</Typography.Title>
      {subtitle && <Typography.Paragraph type="secondary">{subtitle}</Typography.Paragraph>}
    </div>
  </>
}

function Overview ({ summary, datasets, exceptions }) {
  const history = useHistory()
  const attention = exceptions.slice(0, 5)
  const overview = normalizeOverviewSummary(summary, datasets)
  const statusItems = [
    { key: 'healthy', label: '按时', value: overview.healthyCount, note: '按导入计划或更新周期按时上传' },
    { key: 'warning', label: '超期', value: overview.warningCount, note: '已超过计划截止时间或更新周期' },
    { key: 'unknown', label: '未设置周期', value: overview.unknownCount, note: '没有导入计划且更新频率无法计算' }
  ]
  const kpis = [
    { key: 'total', label: '数据源总数', value: overview.datasetCount, note: '当前账号可见的 AIC 数据源' },
    ...statusItems,
    { key: 'unconfigured', label: '未配置检测规则', value: overview.unconfiguredRuleDatasetCount, note: '三类检测规则均未启用' }
  ]
  const attentionDescription = overview.warningCount > 0 || overview.unknownCount > 0 || overview.unconfiguredRuleDatasetCount > 0
    ? `暂无已生成异常；当前另有 ${formatInteger(overview.warningCount, '0')} 个超期未上传、${formatInteger(overview.unknownCount, '0')} 个未设置上传周期`
    : '当前没有待关注异常'
  return <div>
    <Header
      active="overview"
      title="数据可靠性"
      subtitle={<>{healthyRateSummary(summary.healthyRate)} · {display(overview.datasetCount)} 个 AIC 数据源 · {exceptions.length} 个异常</>}
    />
    <Row gutter={[16, 16]} className="aic-kpis" aria-label="数据可靠性实时统计">
      {kpis.map(item => <Col key={item.key} xs={24} sm={12} lg={6}>
        <Card className={`aic-kpi aic-kpi-${item.key}`}>
          <div className="aic-kpi-label">{item.label}</div>
          <div className="aic-kpi-value">{formatInteger(item.value)}</div>
          <div className="aic-kpi-note">{item.note}</div>
        </Card>
      </Col>)}
    </Row>
    <div className="aic-overview-panels">
      <Card className="aic-panel aic-overview-attention" title="需要关注" extra={<Button type="link" onClick={() => history.push('/portal/aic-dashboard/exceptions')}>查看异常中心</Button>}>
        <Table
          size="small"
          pagination={false}
          dataSource={attention}
          rowKey={row => row.id || row.projectId}
          columns={[
            { title: '问题', render: (_, row) => display(row.title || row.type) },
            { title: '状态', dataIndex: 'status', render: display },
            { title: '影响', dataIndex: 'impact', render: display },
            { title: '登记人', dataIndex: 'owner', render: display },
            { title: '操作', render: () => <Button type="link" onClick={() => history.push('/portal/aic-dashboard/exceptions')}>查看</Button> }
          ]}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={attentionDescription}><Button onClick={() => history.push('/portal/aic-dashboard/datasets')}>查看数据源</Button></Empty> }}
        />
      </Card>
      <Card className="aic-panel aic-overview-distribution" title={healthTitle('当前健康状态')} extra={<span className="aic-muted">AIC 实时快照</span>}>
        {overview.datasetCount > 0 ? <div className="aic-health-distribution">
          <div className="aic-health-distribution-bar" aria-label={`按时 ${overview.healthyCount}，超期 ${overview.warningCount}，未设置周期 ${overview.unknownCount}`}>
            {statusItems.filter(item => item.value > 0).map(item => <span
              key={item.key}
              className={`aic-health-distribution-${item.key}`}
              style={{ width: `${(item.value / overview.datasetCount) * 100}%` }}
            />)}
          </div>
          <div className="aic-health-distribution-legend">
            {statusItems.map(item => <div key={item.key}>
              <span className={`aic-health-dot aic-health-dot-${item.key}`} />
              <span>{item.label}</span>
              <strong>{formatInteger(item.value, '0')}</strong>
              <small>{item.note}</small>
            </div>)}
          </div>
          {overview.unconfiguredRuleDatasetCount !== null && <div className="aic-health-distribution-note">
            {formatInteger(overview.unconfiguredRuleDatasetCount, '0')} 个数据源尚未启用导入计划、数据日期或质量检测规则；该数量与健康状态可能重叠。
          </div>}
        </div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前没有可见数据源" />}
      </Card>
    </div>
  </div>
}

function Catalog ({ datasets }) {
  const history = useHistory()
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState('all')
  const [sensitivity, setSensitivity] = useState('all')
  const filtered = useMemo(() => datasets.filter(dataset => (
    (!keyword || [dataset.projectName, dataset.dataOwner, dataset.owner, dataset.ownerDepartmentName, dataset.businessSubject].join(' ').toLowerCase().includes(keyword.toLowerCase())) &&
    (status === 'all' || getHealthStatus(dataset) === status) &&
    (sensitivity === 'all' || dataset.sensitivityLevel === sensitivity)
  )), [datasets, keyword, status, sensitivity])
  const columns = [
    {
      title: '数据源名称', dataIndex: 'projectName', width: 260,
      render: (value, row) => <div className="aic-dataset-name">
        <span className="aic-dataset-name-text">{display(value)}</span>
        <Tag className="aic-dataset-kind-tag" color={row.projectKind === 'application' ? 'blue' : 'cyan'}>{row.projectKind === 'application' ? 'Vibe' : 'AIC'}</Tag>
      </div>
    },
    { title: '部门', dataIndex: 'ownerDepartmentName', width: 120, render: value => display(value) },
    { title: '上传人', dataIndex: 'dataOwner', width: 140, ellipsis: true, render: value => display(value) },
    { title: '登记人', dataIndex: 'owner', width: 90, render: value => display(value) },
    { title: '数据行数', dataIndex: 'dataRowCount', width: 110, align: 'right', render: value => formatInteger(value) },
    {
      title: healthTitle('健康状态'),
      width: 100,
      render: (_, row) => {
        if (row.projectKind === 'application' && !row.healthStatus) return '-'
        const meta = healthMeta(getHealthStatus(row))
        return <Badge status={meta.status} text={meta.label} />
      }
    },
    { title: '最后上传日期', dataIndex: 'lastSuccessfulUploadAt', width: 130, render: (value, row) => formatDate(value, row.projectKind === 'application' ? '-' : '从未上传过数据') },
    { title: '下次上传日期', dataIndex: 'nextExpectedUploadAt', width: 130, render: value => formatDate(value, '-') },
    {
      title: '数据最新日期', dataIndex: 'latestDataDate', width: 150,
      render: (value, row) => row.projectKind === 'application' && !row.latestDataDateStatus ? '-' : row.latestDataDateStatus ? formatDate(value, row.latestDataDateStatus === 'unknown' ? '暂无数据' : '-') : pendingText
    },
    // 暂时注释「异常数据」「质量状态」两列：绝大多数数据源尚未配置质量规则，列内基本是空态。
    // { title: '异常数据', dataIndex: 'qualityErrorCount', width: 95, render: (value, row) => row.projectKind === 'application' && !row.qualityStatus ? '-' : row.qualityStatus ? (value ?? '-') : waitingText },
    // {
    //   title: '质量状态', dataIndex: 'qualityStatus', width: 105,
    //   render: (value, row) => {
    //     if (row.projectKind === 'application' && !value) return '-'
    //     const meta = reliabilityStatusMeta(value)
    //     return <Badge status={meta.status} text={meta.label} />
    //   }
    // },
    { title: '操作', width: 70, render: (_, row) => <Button type="link" onClick={() => history.push(`/portal/aic-dashboard/datasets/${row.projectId}`)}>详情</Button> }
  ]
  return <div>
    <Nav active="datasets" />
    <div className="aic-toolbar">
      <Input allowClear prefix={<SearchOutlined />} placeholder="搜索数据源、部门、上传人或登记人" value={keyword} onChange={event => setKeyword(event.target.value)} />
      <Select className="aic-filter-select" value={status} onChange={setStatus} options={[
        ['all', '全部健康状态'], ['healthy', '按时'], ['warning', '超期'], ['failing', '故障'], ['unknown', '未设置周期']
      ].map(([value, label]) => ({ value, label }))} />
      <Select className="aic-filter-select" value={sensitivity} onChange={setSensitivity} options={[
        { value: 'all', label: '全部敏感级' }, { value: 'L1', label: 'L1 · 全员可见' }, { value: 'L2', label: 'L2 · 部门可见' }, { value: 'L3', label: 'L3 · 授权可见' }
      ]} />
    </div>
    <Card className="aic-panel" title={`我可见的数据源（${filtered.length}/${datasets.length}）`}>
      <Table
        rowKey={row => `${row.projectKind || 'dataset'}-${row.projectId}`}
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 1300 }}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        locale={{ emptyText: <Empty description="当前没有符合条件的数据源" /> }}
      />
    </Card>
  </div>
}

function Detail () {
  const { projectId } = useParams()
  const history = useHistory()
  const [activeAnchor, setActiveAnchor] = useState('data')
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [codexGuideVisible, setCodexGuideVisible] = useState(false)
  const anchors = [['data', '数据'], ['health', '健康'], ['reliability', '可靠性规则'], ['quality', '业务数据检测'], ['structure', '结构'], ['dictionary', '数据字典'], ['runtime', '运行'], ['audit', '权限与审计']]

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setItem(null)
    setActiveAnchor('data')
    setError('')
    getAicDashboardDatasetDetail(projectId)
      .then(value => { if (mounted) setItem(value && value.projectId ? value : null) })
      .catch(() => { if (mounted) setError('数据源详情加载失败') })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [projectId])

  const jumpTo = key => {
    setActiveAnchor(key)
    const target = document.getElementById(`aic-detail-${key}`)
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  if (loading) return <><Nav active="datasets" /><div className="aic-detail-loading"><Spin /></div></>
  if (!item) return <><Nav active="datasets" />{error && <Alert type="error" showIcon message={error} />}<Card className="aic-panel"><Empty description="数据源不存在或无法读取" /><Button onClick={() => history.push('/portal/aic-dashboard/datasets')}>返回数据源</Button></Card></>

  if (item.projectKind === 'application') return <div>
    <Nav active="datasets" />
    <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => history.push('/portal/aic-dashboard/datasets')}>数据源</Button>
    <Typography.Title level={1}>{display(item.projectName)}</Typography.Title>
    <nav className="aic-tabs aic-anchor-tabs" aria-label="数据源详情导航">
      {[['data', '数据'], ['basics', '基本信息']].map(([key, label]) => <button key={key} className={activeAnchor === key ? 'active' : ''} onClick={() => jumpTo(key)} aria-current={activeAnchor === key ? 'location' : undefined}>{label}</button>)}
    </nav>
    <div id="aic-detail-data" className="aic-detail-section"><ProjectData key={projectId} projectId={projectId} /></div>
    <div id="aic-detail-basics" className="aic-detail-section"><Card className="aic-panel" title="基本信息">
      <div className="aic-description-grid">
        <span>系统类型</span><b>Vibe</b>
        <span>部门</span><b>{display(item.ownerDepartmentName)}</b>
        <span>上传人</span><b>{display(item.dataOwner)}</b>
        <span>登记人</span><b>{display(item.owner)}</b>
      </div>
    </Card></div>
  </div>

  return <div>
    <Nav active="datasets" />
    <div className="aic-detail-head">
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => history.push('/portal/aic-dashboard/datasets')}>数据源</Button>
      <Typography.Title level={1}>{display(item.projectName)}</Typography.Title>
      <div className="aic-detail-meta"><span>登记人：{display(item.owner)}</span></div>
    </div>
    <nav className="aic-tabs aic-anchor-tabs" aria-label="数据源详情导航">
      {anchors.map(([key, label]) => <button key={key} className={activeAnchor === key ? 'active' : ''} onClick={() => jumpTo(key)} aria-current={activeAnchor === key ? 'location' : undefined}>{label}</button>)}
    </nav>
    <div id="aic-detail-data" className="aic-detail-section"><ProjectData key={projectId} projectId={projectId} /></div>
    <div id="aic-detail-health" className="aic-detail-section"><DetailTab type="health" item={item} /></div>
    <div id="aic-detail-reliability" className="aic-detail-section"><DetailTab type="reliability" item={item} /></div>
    <div id="aic-detail-quality" className="aic-detail-section"><DetailTab type="quality" item={item} /></div>
    <div id="aic-detail-structure" className="aic-detail-section"><DetailTab type="structure" item={item} /></div>
    <div id="aic-detail-dictionary" className="aic-detail-section"><ProjectDictionary item={item} /></div>
    <div id="aic-detail-runtime" className="aic-detail-section"><DetailTab type="runtime" item={item} /></div>
    <div id="aic-detail-audit" className="aic-detail-section"><DetailTab type="audit" item={item} onOpenCodexGuide={() => setCodexGuideVisible(true)} /></div>
    <VibeCodexGuideDialog visible={codexGuideVisible} scene={VIBE_CODEX_GUIDE_SCENE.NORMAL} onClose={() => setCodexGuideVisible(false)} />
  </div>
}

function DetailTab ({ type, item, onOpenCodexGuide }) {
  if (type === 'reliability') return <ReliabilityRules item={item} />
  if (type === 'quality') {
    const qualityMeta = reliabilityStatusMeta(item.qualityStatus)
    const latestDataDate = item.latestDataDateStatus
      ? formatDateTime(item.latestDataDate, item.latestDataDateStatus === 'unknown' ? '暂无数据' : '-')
      : pendingText
    return <Card className="aic-panel" title="业务数据检测">
      <div className="aic-description-grid">
        <span>最新数据截止日期</span><b>{latestDataDate}</b>
        <span>数据日期检测时间</span><b>{item.latestDataDateStatus ? formatDateTime(item.latestDataDateCheckedAt, '-') : waitingText}</b>
        <span>异常数据数量</span><b>{item.qualityStatus ? (item.qualityErrorCount ?? '-') : waitingText}</b>
        <span>质量状态</span><b><Badge status={qualityMeta.status} text={qualityMeta.label} /></b>
        <span>质量检测时间</span><b>{item.qualityStatus ? formatDateTime(item.qualityCheckedAt, '-') : waitingText}</b>
        <span>检测结果</span><b>{qualityResultText(item)}</b>
      </div>
    </Card>
  }
  if (type === 'structure') {
    const fields = Array.isArray(item.fields) ? item.fields : []
    return <Card className="aic-panel" title="字段字典">
      <Table
        size="small"
        pagination={false}
        rowKey={field => field.fieldId || `${field.tableId}-${field.fieldName}`}
        columns={[
          { title: '字段名', dataIndex: 'fieldName', render: display },
          { title: '业务含义', render: (_, field) => display(field.description || field.displayName) },
          { title: '类型', dataIndex: 'dataType', render: display },
          { title: '质量规则', dataIndex: 'qualityConfigured', render: configured => <Badge status={configured ? 'success' : 'default'} text={configured ? '已配置' : '未配置'} /> }
        ]}
        dataSource={fields}
        locale={{ emptyText: <Empty description="从未上传过数据" /> }}
      />
    </Card>
  }
  if (type === 'runtime') return <Card className="aic-panel" title="运行记录"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="数据导入类，无运行记录" /></Card>
  if (type === 'audit') return <Card className="aic-panel" title="权限与审计">
    <div className="aic-description-grid">
      <span>数据级别</span><b>{display(item.sensitivityLevel)}</b>
      <span>访问方式</span><b><AicSkillEntryButton /></b>
      <span>最近审计</span><b>无</b>
      <span>审计结论</span><b>无</b>
    </div>
  </Card>
  const meta = healthMeta(getHealthStatus(item))
  return <Card className="aic-panel" title="关键信息">
    <div className="aic-description-grid">
      <span>部门</span><b>{display(item.ownerDepartmentName)}</b>
      <span>敏感级</span><b>{display(item.sensitivityLevel)}</b>
      {healthTitle('健康状态')}<b><Badge status={meta.status} text={meta.label} /></b>
      <span>DELAY 状态</span><b>{item.healthReason ? display(item.healthReason) : meta.label}</b>
      <span>最后上传时间</span><b>{formatDateTime(item.lastSuccessfulUploadAt)}</b>
      <span>计划截止时间</span><b>{formatDateTime(item.nextExpectedUploadAt, '-')}</b>
      <span>更新频率</span><b>{formatUpdateFrequency(item.updateFrequency)}</b>
      <span>超期时长</span><b>{item.overdueHours === null || item.overdueHours === undefined ? '-' : `${item.overdueHours} 小时`}</b>
    </div>
  </Card>
}

function ReliabilityRules ({ item }) {
  const config = item.reliabilityConfig || {}
  const importPlan = config.importPlan
  const dataDateField = config.dataDateField
  const qualityRules = Array.isArray(config.qualityRules) ? config.qualityRules : []
  const schedules = importPlan && Array.isArray(importPlan.schedules) ? importPlan.schedules.slice(0, 5) : []
  const qualityMeta = reliabilityStatusMeta(item.qualityStatus)

  return <Card className="aic-panel aic-reliability-panel" title="可靠性规则" extra={<Tag color="blue">只读</Tag>}>
    <Alert
      className="aic-readonly-note"
      type="info"
      showIcon
      message="展示当前已启用规则"
      description="这里只展示已经确认并参与监控的版本，不包含草稿、历史版本和生成 SQL。"
    />

    <section className="aic-reliability-block">
      <div className="aic-reliability-block-head">
        <div><h3>导入计划</h3><span>用于判断数据是否按计划上传</span></div>
        {importPlan && <Tag color="green">v{importPlan.version} · 已启用</Tag>}
      </div>
      {importPlan ? <>
        <div className="aic-description-grid aic-rule-description-grid">
          <span>导入周期</span><b>{display(importPeriodLabels[importPlan.periodType] || importPlan.periodType)}</b>
          <span>时区</span><b>{display(importPlan.timezone)}</b>
          <span>规则说明</span><b>{display(importPlan.naturalLanguageRule)}</b>
          <span>宽限时间</span><b>{importPlan.gracePeriodHours === null || importPlan.gracePeriodHours === undefined ? '-' : `${importPlan.gracePeriodHours} 小时`}</b>
          <span>当前计划截止</span><b>{formatDateTime(item.nextExpectedUploadAt, '-')}</b>
          <span>确认时间</span><b>{formatDateTime(importPlan.confirmedAt, '-')}</b>
        </div>
        {schedules.length > 0 && <div className="aic-readonly-table">
          <div className="aic-readonly-table-title">导入计划（展示前 {schedules.length} 期，共 {importPlan.schedules.length} 期）</div>
          <Table
            size="small"
            pagination={false}
            rowKey={row => row.scheduleId || row.sequenceNo}
            dataSource={schedules}
            columns={[
              { title: '期次', dataIndex: 'periodKey', render: display },
              { title: '周期开始', dataIndex: 'windowStartAt', render: value => formatDateTime(value, '-') },
              { title: '导入截止', dataIndex: 'deadlineAt', render: value => formatDateTime(value, '-') },
              { title: '宽限截止', dataIndex: 'graceDeadlineAt', render: value => formatDateTime(value, '-') },
              { title: '状态', dataIndex: 'status', render: value => display(scheduleStatusLabels[value] || value) }
            ]}
          />
        </div>}
      </> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未启用导入计划" />}
    </section>

    <section className="aic-reliability-block">
      <div className="aic-reliability-block-head">
        <div><h3>数据日期字段</h3><span>字段最大值代表该数据源的最新业务日期</span></div>
        {dataDateField && <Tag color="green">v{dataDateField.version} · 已启用</Tag>}
      </div>
      {dataDateField ? <div className="aic-description-grid aic-rule-description-grid">
        <span>逻辑表</span><b>{tableDisplay(dataDateField)}</b>
        <span>数据日期字段</span><b>{fieldDisplay(dataDateField)}</b>
        <span>字段类型</span><b>{display(dataDateField.fieldDataType)}</b>
        <span>日期粒度</span><b>{display(dataDateField.granularity)}</b>
        <span>取值方式</span><b>取该字段最大值</b>
        <span>最新数据日期</span><b>{item.latestDataDateStatus ? formatDateTime(item.latestDataDate, '暂无数据') : '待执行'}</b>
        <span>安全校验</span><b>{display(validationStatusLabels[dataDateField.validationStatus] || dataDateField.validationStatus)}</b>
        <span>最近检测</span><b>{formatDateTime(item.latestDataDateCheckedAt, '-')}</b>
      </div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未启用数据日期字段" />}
    </section>

    <section className="aic-reliability-block">
      <div className="aic-reliability-block-head">
        <div><h3>字段质量预警</h3><span>命中规则的数据将进入异常判断</span></div>
        {qualityRules.length > 0 && <Badge status={qualityMeta.status} text={`${qualityRules.length} 条已启用规则`} />}
      </div>
      {qualityRules.length > 0 ? <>
        <div className="aic-quality-result-summary">
          <span>最近检测结果</span>
          <b><Badge status={qualityMeta.status} text={qualityResultText(item)} /></b>
          <span>检测时间</span>
          <b>{formatDateTime(item.qualityCheckedAt, '-')}</b>
        </div>
        <div className="aic-readonly-table">
          <Table
            size="small"
            pagination={false}
            rowKey={row => row.ruleId || row.ruleKey}
            dataSource={qualityRules}
            columns={[
              { title: '规则名称', dataIndex: 'ruleName', render: display },
              { title: '逻辑表', render: (_, row) => tableDisplay(row) },
              { title: '字段', render: (_, row) => fieldDisplay(row) },
              { title: '规则说明', dataIndex: 'description', render: display },
              { title: '级别', dataIndex: 'severity', render: display },
              { title: '版本', dataIndex: 'version', render: value => value ? `v${value}` : '-' },
              { title: '安全校验', dataIndex: 'validationStatus', render: value => display(validationStatusLabels[value] || value) }
            ]}
          />
        </div>
      </> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未启用字段质量预警规则" />}
    </section>
  </Card>
}

function tableDisplay (rule) {
  if (!rule) return '-'
  if (rule.tableDisplayName && rule.tableName && rule.tableDisplayName !== rule.tableName) return `${rule.tableDisplayName}（${rule.tableName}）`
  return display(rule.tableDisplayName || rule.tableName)
}

function fieldDisplay (rule) {
  if (!rule) return '-'
  if (rule.fieldDisplayName && rule.fieldName && rule.fieldDisplayName !== rule.fieldName) return `${rule.fieldDisplayName}（${rule.fieldName}）`
  return display(rule.fieldDisplayName || rule.fieldName)
}

function normalizeRuntimeException (item) {
  const failed = item.healthStatus === 'failure'
  return {
    ...item,
    id: `runtime-${item.id}`,
    runtimeAssetId: item.id,
    isRuntimeAsset: true,
    title: `${display(item.name)} · ${failed ? '运行失败' : '运行超期'}`,
    type: failed ? '运行资产故障' : '运行资产超期',
    status: '待处理',
    severity: failed ? '高' : '中',
    impact: [item.systemName, item.environment].filter(Boolean).join(' · ') || display(item.assetType),
    owner: item.ownerName,
    firstSeenAt: failed ? item.lastFailureAt : item.nextExpectedAt,
    lastSeenAt: item.evaluatedAt,
    evidence: [
      item.healthReason,
      item.lastSuccessAt && `最近成功：${formatDateTime(item.lastSuccessAt, '-')}`,
      item.nextExpectedAt && `下次期望：${formatDateTime(item.nextExpectedAt, '-')}`,
      item.lastFailureAt && `最近失败：${formatDateTime(item.lastFailureAt, '-')}`,
      item.lastErrorSummary && `错误摘要：${item.lastErrorSummary}`
    ].filter(Boolean)
  }
}

function Exceptions ({ exceptions }) {
  const [selectedId, setSelectedId] = useState(exceptions[0] && exceptions[0].id)
  const [keyword, setKeyword] = useState('')
  const [type, setType] = useState('all')
  const [status, setStatus] = useState('all')
  const [owner, setOwner] = useState('all')
  const typeOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.type).filter(Boolean))), [exceptions])
  const statusOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.status).filter(Boolean))), [exceptions])
  const ownerOptions = useMemo(() => Array.from(new Set(exceptions.map(item => item.owner).filter(Boolean))), [exceptions])
  const filtered = exceptions.filter(item => (
    (!keyword || [item.title, item.owner, item.impact, item.healthReason].join(' ').toLowerCase().includes(keyword.toLowerCase())) &&
    (type === 'all' || item.type === type) &&
    (status === 'all' || item.status === status) &&
    (owner === 'all' || item.owner === owner)
  ))
  const selected = filtered.find(item => String(item.id) === String(selectedId)) || filtered[0]
  const evidence = selected && Array.isArray(selected.evidence) ? selected.evidence : []
  const timeline = selected && Array.isArray(selected.timeline) ? selected.timeline : []
  return <div>
    <Nav active="exceptions" />
    <div className="aic-exception-toolbar">
      <Select value={type} onChange={setType} options={[{ value: 'all', label: '全部类型' }, ...typeOptions.map(value => ({ value, label: value }))]} />
      <Select value={status} onChange={setStatus} options={[{ value: 'all', label: '全部状态' }, ...statusOptions.map(value => ({ value, label: value }))]} />
      <Select value={owner} onChange={setOwner} options={[{ value: 'all', label: '全部负责人' }, ...ownerOptions.map(value => ({ value, label: value }))]} />
      <Input value={keyword} onChange={event => setKeyword(event.target.value)} prefix={<SearchOutlined />} placeholder="搜索异常、影响或负责人" allowClear />
    </div>
    <Alert className="aic-readonly-note" showIcon type="info" message="异常中心当前为只读视图" description="接手、恢复和通知历史尚无后端操作接口，页面不会伪造处理结果。" />
    <div className="aic-exception-layout">
      <Card className="aic-exception-list" title={`异常列表（${filtered.length}）`}>
        {filtered.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无异常记录" />}
        {filtered.map(item => <button key={item.id} className={`aic-exception-item ${selected && selected.id === item.id ? 'selected' : ''}`} onClick={() => setSelectedId(item.id)}>
          <div className="aic-exception-item-title">{display(item.title)}</div>
          <div className="aic-exception-item-meta"><Tag color={item.severity === 'critical' || item.severity === '高' ? 'red' : 'orange'}>{display(item.severity)}</Tag><span>{display(item.status)}</span><span>{formatDateTime(item.lastSeenAt, '-')}</span></div>
        </button>)}
      </Card>
      <Card className="aic-exception-detail" title="异常详情">
        {selected ? <>
          <div className="aic-detail-title-row"><Typography.Title level={3}>{display(selected.title)}</Typography.Title><Tag color={selected.severity === 'critical' || selected.severity === '高' ? 'red' : 'orange'}>{display(selected.severity)}风险</Tag></div>
          <div className="aic-description-grid">
            <span>异常类型</span><b>{display(selected.type)}</b>
            <span>影响范围</span><b>{display(selected.impact)}</b>
            <span>负责人</span><b>{display(selected.owner)}</b>
            <span>当前状态</span><b>{display(selected.status)}</b>
            <span>{selected.isRuntimeAsset ? '最近成功时间' : '最后上传时间'}</span><b>{formatDateTime(selected.isRuntimeAsset ? selected.lastSuccessAt : selected.lastSuccessfulUploadAt)}</b>
            <span>{selected.isRuntimeAsset ? '下次期望时间' : '计划截止时间'}</span><b>{formatDateTime(selected.isRuntimeAsset ? selected.nextExpectedAt : selected.nextExpectedUploadAt, '-')}</b>
            <span>首次发现</span><b>{formatDateTime(selected.firstSeenAt, '-')}</b>
            <span>最近发现</span><b>{formatDateTime(selected.lastSeenAt, '-')}</b>
            <span>恢复时间</span><b>{formatDateTime(selected.recoveredAt, '未恢复')}</b>
            <span>超期时长</span><b>{selected.overdueHours === null || selected.overdueHours === undefined ? '-' : `${selected.overdueHours} 小时`}</b>
          </div>
          <Card size="small" title="判定依据"><ul>{evidence.length > 0 ? evidence.map((value, index) => <li key={`evidence-${index}`}>{display(value)}</li>) : <li>暂无判定依据</li>}</ul></Card>
          {timeline.length > 0 && <Timeline className="aic-timeline" items={timeline.map((value, index) => ({ key: `timeline-${index}`, children: display(value) }))} />}
        </> : <Empty description="暂无异常记录" />}
      </Card>
    </div>
  </div>
}

export default function AicDashboard () {
  const location = useLocation()
  const [summary, setSummary] = useState({})
  const [datasets, setDatasets] = useState([])
  const [exceptions, setExceptions] = useState([])
  const [runtimeExceptions, setRuntimeExceptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    Promise.all([
      settleRequest(getAicDashboardSummary({})),
      settleRequest(getAicDashboardDatasets({})),
      settleRequest(getAicDashboardExceptions({})),
      settleRequest(getRuntimeAssetExceptions({}))
    ])
      .then(([summaryResult, datasetResult, exceptionResult, runtimeExceptionResult]) => {
        if (summaryResult.ok) setSummary(summaryResult.value || {})
        if (datasetResult.ok) setDatasets(unwrapList(datasetResult.value))
        if (exceptionResult.ok) setExceptions(unwrapList(exceptionResult.value))
        if (runtimeExceptionResult.ok) setRuntimeExceptions(unwrapList(runtimeExceptionResult.value).map(normalizeRuntimeException))
        const failed = [
          !summaryResult.ok && '总览',
          !datasetResult.ok && '数据源',
          !exceptionResult.ok && 'AIC 异常',
          !runtimeExceptionResult.ok && '运行资产异常'
        ].filter(Boolean)
        setError(failed.length > 0 ? `${failed.join('、')}数据加载失败，其余内容仍可使用` : '')
      })
      .finally(() => setLoading(false))
  }, [])

  const path = location.pathname
  const page = path.includes('/settings')
    ? 'settings'
    : path.includes('/assets')
      ? 'assets'
    : path.includes('/exceptions')
      ? 'exceptions'
      : path.match(/\/datasets\/[^/]+/)
        ? 'detail'
        : path.includes('/datasets') ? 'datasets' : 'overview'

  return <Layout className="aic-dashboard">
    <Spin spinning={loading}>
      {error && <Alert type="warning" showIcon message={error} />}
      {page === 'overview' && <Overview summary={summary} datasets={datasets.filter(item => item.projectKind !== 'application')} exceptions={exceptions} />}
      {page === 'datasets' && <Catalog datasets={datasets} />}
      {page === 'detail' && <Detail />}
      {page === 'assets' && <RuntimeAssets Nav={Nav} />}
      {page === 'exceptions' && <Exceptions exceptions={[...exceptions, ...runtimeExceptions]} />}
      {page === 'settings' && <AicReliabilitySettings datasets={datasets.filter(item => item.projectKind !== 'application')} Nav={Nav} />}
    </Spin>
  </Layout>
}
